import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import {
  HeartPulse, Shield, Car, Home as HomeIcon, Scale, Gavel, Plane, Building2,
  ArrowUpRight, Check, Clock, Users, Sparkles, TrendingUp, FileText,
} from "lucide-react";

const iconFor = {
  krankenversicherung: HeartPulse,
  lebensversicherung: Shield,
  motorfahrzeug: Car,
  hausrat: HomeIcon,
  privathaftpflicht: Scale,
  rechtsschutz: Gavel,
  reise: Plane,
  kmu: Building2,
  steuererklaerung: FileText,
};

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function Home() {
  const { t } = useLang();

  return (
    <div data-testid="page-home" className="bg-white text-[#0A0A0A]">
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 pb-24 md:pt-24 md:pb-32">
        {/* Ambient gradient blobs */}
        <div className="absolute top-20 -right-40 w-[600px] h-[600px] bg-[#EA6A1A]/8 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute -top-20 -left-40 w-[500px] h-[500px] bg-[#EA6A1A]/5 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex justify-center mb-8"
          >
            <div className="inline-flex items-center gap-2 bg-[#F5F5F4] border border-[#E9E9E7] px-4 py-2 rounded-full" data-testid="hero-eyebrow">
              <span className="w-2 h-2 bg-[#22C55E] rounded-full animate-pulse" />
              <span className="text-[13px] text-[#52525B]">{t.hero.eyebrow} · Est. 2020</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="font-display text-center text-6xl md:text-8xl lg:text-[112px] leading-[0.95] tracking-[-0.03em] font-medium max-w-6xl mx-auto"
            data-testid="hero-title"
          >
            {t.hero.title}{" "}
            <span className="italic text-[#EA6A1A] font-normal">{t.hero.titleAccent.replace(".", "")}</span>
            <span className="text-[#EA6A1A]">.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-8 text-center text-lg md:text-xl text-[#52525B] max-w-3xl mx-auto leading-[1.6]"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-12 flex flex-wrap gap-3 justify-center items-center"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#EA6A1A] text-white px-6 py-3.5 rounded-full transition-all shadow-sm"
            >
              <span className="text-sm font-medium">{t.hero.primary}</span>
              <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 border border-[#E9E9E7] hover:border-[#0A0A0A] text-[#0A0A0A] px-6 py-3.5 rounded-full transition-all"
            >
              <span className="text-sm font-medium">{t.hero.secondary}</span>
            </Link>
          </motion.div>

          {/* Trust bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="mt-16 flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-sm text-[#71717A]"
          >
            {t.trust.items.map((item, i) => (
              <div key={i} className="inline-flex items-center gap-2">
                <Check size={14} className="text-[#EA6A1A]" strokeWidth={2.5} />
                <span>{item.t}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <motion.div {...fadeUp} className="grid md:grid-cols-12 gap-8 mb-16 items-end">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 bg-[#FFF1E6] border border-[#EA6A1A]/20 text-[#EA6A1A] px-3 py-1.5 rounded-full text-xs font-medium mb-5" data-testid="services-eyebrow">
                <span className="w-1.5 h-1.5 bg-[#EA6A1A] rounded-full" />
                {t.servicesPreview.eyebrow}
              </div>
              <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-[-0.02em] font-medium max-w-3xl">
                {t.servicesPreview.title.split(" ").slice(0, -1).join(" ")}{" "}
                <span className="italic text-[#EA6A1A] font-normal">{t.servicesPreview.title.split(" ").slice(-1)}</span>
              </h2>
            </div>
            <div className="md:col-span-4 md:col-start-9">
              <p className="text-[#52525B] leading-relaxed">{t.servicesPreview.subtitle}</p>
            </div>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="services-grid">
            {t.servicesList.map((s, i) => {
              const Icon = iconFor[s.id] || Shield;
              return (
                <motion.div
                  key={s.id}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: (i % 4) * 0.06 }}
                  data-testid={`service-tile-${s.id}`}
                >
                  <Link
                    to="/services"
                    className="group flex flex-col h-full bg-white border border-[#E9E9E7] rounded-2xl p-6 hover:border-[#0A0A0A] hover:-translate-y-1 transition-all duration-300 card-shadow"
                  >
                    <div className="flex items-center justify-between mb-8">
                      <div className="w-11 h-11 bg-[#F5F5F4] group-hover:bg-[#EA6A1A] rounded-xl flex items-center justify-center transition-colors">
                        <Icon className="text-[#0A0A0A] group-hover:text-white transition-colors" size={20} strokeWidth={1.6} />
                      </div>
                      <ArrowUpRight
                        className="text-[#71717A] group-hover:text-[#0A0A0A] group-hover:rotate-45 transition-all"
                        size={18}
                      />
                    </div>
                    <h3 className="font-display text-xl font-medium mb-2 tracking-tight">{s.t}</h3>
                    <p className="text-sm text-[#71717A] leading-relaxed flex-1">{s.d}</p>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-12 flex justify-center">
            <Link
              to="/services"
              data-testid="services-cta"
              className="inline-flex items-center gap-2 border border-[#E9E9E7] hover:border-[#0A0A0A] px-5 py-2.5 rounded-full text-sm transition-all"
            >
              {t.servicesPreview.cta}
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* STEUERERKLÄRUNG PRICING */}
      <section className="py-24 md:py-32 bg-white relative overflow-hidden">
        <div className="absolute top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-[#EA6A1A]/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-3 py-1.5 rounded-full text-xs font-medium mb-6">
              <FileText size={12} strokeWidth={2} />
              {t.tax.eyebrow}
            </div>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-[-0.02em] font-medium mb-6">
              {t.tax.title.split(" ").slice(0, -1).join(" ")}{" "}
              <span className="italic text-[#EA6A1A] font-normal">{t.tax.title.split(" ").slice(-1)}</span>
            </h2>
            <p className="text-lg text-[#52525B] leading-relaxed">{t.tax.subtitle}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {t.tax.tiers.map((tier, i) => (
              <motion.div
                key={tier.name}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                data-testid={`tax-tier-${i}`}
                className={`relative flex flex-col rounded-3xl p-8 md:p-10 transition-all duration-300 ${
                  tier.featured
                    ? "bg-[#0A0A0A] text-white border-2 border-[#EA6A1A] md:-mt-4 md:mb-4 shadow-[0_24px_60px_-12px_rgba(234,106,26,0.35)]"
                    : "bg-white border border-[#E9E9E7] card-shadow hover:-translate-y-1"
                }`}
              >
                {tier.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#EA6A1A] text-white text-xs font-medium px-4 py-1.5 rounded-full">
                    {t.tax.badge}
                  </div>
                )}

                <div className="mb-6">
                  <h3 className={`font-display text-2xl md:text-3xl font-medium tracking-tight mb-2 ${tier.featured ? "text-white" : "text-[#0A0A0A]"}`}>
                    {tier.name}
                  </h3>
                  <p className={`text-sm leading-relaxed ${tier.featured ? "text-white/70" : "text-[#71717A]"}`}>
                    {tier.desc}
                  </p>
                </div>

                <div className="flex items-baseline gap-2 mb-8 pb-8 border-b border-current/10">
                  <span className={`text-xs uppercase tracking-wider ${tier.featured ? "text-white/60" : "text-[#71717A]"}`}>
                    {t.tax.currency}
                  </span>
                  <span className="font-display text-6xl md:text-7xl font-semibold tracking-tighter">
                    {tier.price}
                  </span>
                  <span className={`text-lg ${tier.featured ? "text-white/60" : "text-[#71717A]"}`}>.–</span>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3 text-[15px]">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        tier.featured ? "bg-[#EA6A1A]" : "bg-[#EA6A1A]/10"
                      }`}>
                        <Check size={12} className={tier.featured ? "text-white" : "text-[#EA6A1A]"} strokeWidth={2.5} />
                      </div>
                      <span className={tier.featured ? "text-white/90" : "text-[#0A0A0A]"}>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/steuer"
                  data-testid={`tax-cta-${i}`}
                  className={`group inline-flex items-center justify-between gap-2 rounded-full px-6 py-3.5 text-sm font-medium transition-all ${
                    tier.featured
                      ? "bg-[#EA6A1A] text-white hover:bg-white hover:text-[#0A0A0A]"
                      : "bg-[#0A0A0A] text-white hover:bg-[#EA6A1A]"
                  }`}
                >
                  <span>{t.tax.cta}</span>
                  <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-xs text-[#71717A] mt-10">{t.tax.note}</p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#F7F5F0] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white border border-[#E9E9E7] px-3 py-1.5 rounded-full text-xs font-medium mb-5 text-[#52525B]">
              — {t.trust.title}
            </div>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-[-0.02em] font-medium">
              {t.trust.headline}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {t.trust.items.map((item, i) => (
              <motion.div
                key={item.k}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                data-testid={`trust-item-${i}`}
                className="relative bg-white rounded-2xl p-8 border border-[#E9E9E7] card-shadow"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="text-[#EA6A1A] font-display text-3xl font-semibold">{item.k}</div>
                  <div className="w-2 h-2 bg-[#EA6A1A] rounded-full mt-3" />
                </div>
                <h3 className="font-display text-2xl font-medium mb-3 tracking-tight">{item.t}</h3>
                <p className="text-[#71717A] leading-relaxed text-[15px]">{item.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY — Feature block */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <motion.div {...fadeUp} className="md:col-span-6">
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&fit=crop&w=1200&h=1200&q=85"
                alt="Beratung"
                className="w-full h-[560px] object-cover"
                data-testid="split-image"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md rounded-2xl p-5 flex items-center gap-4 card-shadow">
                <div className="w-12 h-12 bg-[#EA6A1A] rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="text-white" size={20} strokeWidth={2.5} />
                </div>
                <div>
                  <div className="font-medium text-[15px]">Persönliche Beratung</div>
                  <div className="text-xs text-[#71717A]">Wohlen · Schweiz</div>
                </div>
              </div>
            </div>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="md:col-span-6">
            <div className="inline-flex items-center gap-2 bg-[#F5F5F4] border border-[#E9E9E7] px-3 py-1.5 rounded-full text-xs font-medium mb-6 text-[#52525B]">
              — {t.about.eyebrow}
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em] font-medium mb-8">
              {t.about.title}
            </h2>
            <p className="text-[#52525B] text-lg leading-[1.7] mb-5">{t.about.p1}</p>
            <p className="text-[#71717A] leading-[1.7] mb-10">{t.about.p2}</p>
            <Link
              to="/about"
              data-testid="split-cta"
              className="inline-flex items-center gap-2 text-[#0A0A0A] group"
            >
              <span className="link-under text-sm font-medium">{t.nav.about}</span>
              <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA — Modern gradient card */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <motion.div
            {...fadeUp}
            className="relative overflow-hidden bg-[#0A0A0A] rounded-3xl p-10 md:p-20 text-center"
          >
            {/* Ambient blob */}
            <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#EA6A1A]/30 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-[#EA6A1A]/20 rounded-full blur-[100px] pointer-events-none" />

            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur px-3 py-1.5 rounded-full text-xs font-medium text-white/90 mb-6">
                — {t.contact.eyebrow}
              </div>
              <h2 className="font-display text-5xl md:text-7xl leading-[0.98] tracking-[-0.03em] text-white font-medium max-w-4xl mx-auto mb-6">
                {t.contact.title.replace(".", "")}<span className="italic text-[#EA6A1A]">.</span>
              </h2>
              <p className="text-white/70 text-lg max-w-2xl mx-auto mb-10">{t.contact.subtitle}</p>
              <Link
                to="/contact"
                data-testid="cta-strip-btn"
                className="group inline-flex items-center gap-2 bg-white text-[#0A0A0A] hover:bg-[#EA6A1A] hover:text-white px-7 py-4 rounded-full transition-all"
              >
                <span className="text-sm font-medium">{t.nav.cta}</span>
                <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
